import React from 'react';
import {
  ChevronRight,
  ChevronDown,
  File,
  Folder,
  FolderOpen,
} from 'lucide-react';
import { useFileTreeStore, type FileNode } from '@/stores/useFileTreeStore';
import { useIDEStore } from '@/stores/useIDEStore';
import { apiClient } from '@/services/api';
import { cn } from '@/utils/cn';

interface FileExplorerProps {
  projectId: string;
}

export default function FileExplorer({ projectId }: FileExplorerProps) {
  const { tree, expandedPaths, toggleExpand, loading } = useFileTreeStore();

  React.useEffect(() => {
    useFileTreeStore.getState().loadFiles(projectId);
  }, [projectId]);

  if (loading) {
    return (
      <div className="p-3 text-xs text-muted-foreground">Loading files...</div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 text-xs font-semibold uppercase text-muted-foreground border-b">
        Explorer
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {tree.map((node) => (
          <TreeNode
            key={node.path}
            node={node}
            depth={0}
            expandedPaths={expandedPaths}
            onToggle={toggleExpand}
            projectId={projectId}
          />
        ))}
        {tree.length === 0 && (
          <div className="p-3 text-xs text-muted-foreground">No files</div>
        )}
      </div>
    </div>
  );
}

function TreeNode({
  node,
  depth,
  expandedPaths,
  onToggle,
  projectId,
}: {
  node: FileNode;
  depth: number;
  expandedPaths: Set<string>;
  onToggle: (path: string) => void;
  projectId: string;
}) {
  const { activeFile, openFile } = useIDEStore();
  const isExpanded = expandedPaths.has(node.path);
  const isActive = activeFile === node.path;

  const handleClick = async () => {
    if (node.type === 'directory') {
      onToggle(node.path);
    } else {
      // Load file content and open it
      try {
        const content = await apiClient.getFileContentByPath(projectId, node.path);
        openFile(node.path, content, node.language || 'text');
      } catch {
        // File may not have content yet — fall back to empty
        openFile(node.path, '', node.language || 'text');
      }
    }
  };

  const getIcon = () => {
    if (node.type === 'directory') {
      return isExpanded ? (
        <FolderOpen className="h-4 w-4 text-blue-400" />
      ) : (
        <Folder className="h-4 w-4 text-blue-400" />
      );
    }
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-1 py-0.5 px-2 cursor-pointer text-sm hover:bg-muted/50',
          isActive && 'bg-muted'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
      >
        {node.type === 'directory' ? (
          isExpanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )
        ) : (
          <span className="w-3" />
        )}
        {getIcon()}
        <span className="truncate text-xs">{node.name}</span>
      </div>

      {node.type === 'directory' && isExpanded && node.children && (
        <>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              projectId={projectId}
            />
          ))}
        </>
      )}
    </>
  );
}
